import { useQuery } from '@tanstack/react-query';
import Immutable from 'immutable';

import { EntityMembershipPolicies } from '@graylog/enterprise-api';

import type { EntityMembershipPolicy } from 'collections/types';
import type { PaginatedList, PaginatedResponseType, SearchParams } from 'stores/PaginationTypes';
import { defaultOnError } from 'util/conditional/onError';

const INITIAL_DATA = {
  pagination: { total: 0, page: 1, perPage: 10, query: '', count: 0 },
  attributes: null,
  list: Immutable.List([]),
};

export type PaginatedEntityMembershipPoliciesResponse = PaginatedResponseType & {
  policies: Array<EntityMembershipPolicy>;
};

export const MEMBERSHIP_POLICIES_QUERY_KEY = 'membership-policies';

export const keyFn = (searchParams: SearchParams) => [MEMBERSHIP_POLICIES_QUERY_KEY, searchParams];

export const fetchPaginatedMembershipPolicies = async (
  searchParams: SearchParams,
): Promise<PaginatedList<EntityMembershipPolicy>> =>
  EntityMembershipPolicies.list(
    searchParams.sort.attributeId as any,
    searchParams.page,
    searchParams.pageSize,
    searchParams.query,
    searchParams.sort.direction,
  ).then(({ policies, per_page, page, query, count, total }: PaginatedEntityMembershipPoliciesResponse) => ({
    list: Immutable.List(policies),
    pagination: {
      count,
      total,
      page,
      perPage: per_page,
      query,
    },
  }));

const useMembershiPolicies = (
  searchParams: SearchParams,
  { enabled } = { enabled: true },
): {
  data: PaginatedList<EntityMembershipPolicy>;
  isLoading: boolean;
  refetch: () => void;
} => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: keyFn(searchParams),
    queryFn: () =>
      defaultOnError(
        fetchPaginatedMembershipPolicies(searchParams),
        'Loading Membership policies failed with status',
        'Could not load Membership policies',
      ),
    enabled,
  });

  return {
    data: data ?? INITIAL_DATA,
    isLoading,
    refetch,
  };
};

export default useMembershiPolicies;
